import { ParagraphFonts } from "../Typography";
import type { Selection } from "./types";

export const findCoupledRunners = (selections: Selection[], target: string) => {
  const match = target.match(/^(\d+)/);

  if (!match) return [];

  return selections.filter((item) => {
    const itemMatch = item.number.match(/^(\d+)([A-Z]*)$/);
    return itemMatch && itemMatch[1] === match[1];
  });
};

export const groupCoupledRunners = (selections: Selection[]) => {
  const getBase = (num: string) => num.replace(/[A-Z]$/, "");

  const groups = selections.reduce((acc, sel) => {
    if (!sel.isAlternate) {
      const base = getBase(sel.number);

      if (/^\d+$/.test(base)) {
        const group = acc.get(base) ?? [];
        group.push(sel.number);
        acc.set(base, group);
      }
    }
    return acc;
  }, new Map<string, string[]>());

  return Array.from(groups.values()).filter((group) => group.length > 1);
};

export const shouldSkipAlternate = (
  selections: Selection[],
  scratches: string[],
  alternateNumber: string,
  wagerDetails: boolean,
  showReplacedRunners: boolean
) => {
  let shouldSkip = false;

  const altInfo = selections.reduce(
    (acc, s) => {
      if (s.isAlternate) {
        acc.total += +1;
        if (scratches.includes(s.number)) {
          acc.scratches += 1;
        }
      }
      return acc;
    },
    {
      total: 0,
      scratches: 0
    } as Record<string, number>
  );
  const mainCoupledRunners = groupCoupledRunners(selections);
  const mainScratches = selections.filter(
    (s) => !s.isAlternate && scratches.includes(s.number)
  );
  const everyMainCoupledScratched = mainScratches.every((s) => {
    const baseNumber = s.number.match(/^(\d+)/)?.[1];
    if (!baseNumber) return true;
    const coupled = findCoupledRunners(selections, baseNumber);
    return coupled.every((c) => scratches.includes(c.number));
  });

  if (mainCoupledRunners.length === 0 && mainScratches.length > 0) {
    shouldSkip = true;
  }

  if (mainCoupledRunners.length > 0 && mainScratches.length > 0) {
    shouldSkip =
      everyMainCoupledScratched ||
      mainScratches.length !== mainCoupledRunners.length;
  }

  if (altInfo.total !== altInfo.scratches && wagerDetails) {
    shouldSkip =
      mainScratches.length > 0 &&
      everyMainCoupledScratched &&
      !scratches.includes(alternateNumber);
  }

  if (altInfo.total === altInfo.scratches || !showReplacedRunners) {
    shouldSkip = false;
  }

  return shouldSkip;
};

export const rangeSelections = (
  selections: Selection[],
  scratches: string[],
  wagerDetails: boolean,
  enableMyBetsBehavior: boolean,
  showReplacedRunners: boolean,
  showAllSaddle: boolean
) =>
  selections
    .reduce((ranges: string[][], curr: Selection) => {
      const allSelections =
        showAllSaddle &&
        !wagerDetails &&
        (scratches.length === 0 ||
          scratches.every(
            (item) => selections.find((sel) => sel.number === item)?.isAlternate
          ));

      if (allSelections && !ranges.flat().includes("all")) {
        ranges.push(["all"]);
      }

      if (curr.isAlternate) {
        const shouldSkip = shouldSkipAlternate(
          selections,
          scratches ?? [],
          curr.number,
          wagerDetails,
          showReplacedRunners
        );

        if (shouldSkip) {
          return ranges;
        }
      }

      const lastSubArray: string[] = ranges[ranges.length - 1];
      const prevCurr = (+curr.number - 1).toString();
      const nextCurr = +curr.number + 1;

      const lastSubArrayChar = lastSubArray?.[lastSubArray?.length - 1]; // 4A or 4
      const prevArr = lastSubArrayChar?.match(/[a-zA-Z]+|[0-9]+/g); // ["4","A"] or ["4"]
      const isPrevCoupledRunner = prevArr && prevArr?.length > 1;

      if (
        (curr.winner && enableMyBetsBehavior) ||
        !lastSubArray ||
        scratches.includes(curr.number) ||
        scratches.includes(prevCurr) ||
        (isPrevCoupledRunner &&
          (+curr.number - 1).toString() !== prevArr?.[0]) ||
        (!isPrevCoupledRunner &&
          lastSubArray[lastSubArray.length - 1] !== prevCurr &&
          !Number.isNaN(+curr.number)) ||
        Number.isNaN(nextCurr)
      ) {
        if (!curr.isAlternate && !allSelections) {
          ranges.push([]);
        }
      }

      if (curr.isAlternate && !ranges.flat().includes("|")) {
        if (!wagerDetails && selections.every((item) => item.isAlternate)) {
          ranges.push(["?"]);
        }
        ranges.push(["|"]);
      }

      if (!allSelections) {
        if (curr.isAlternate) {
          ranges.push([curr.number]);
        } else {
          ranges[ranges.length - 1].push(curr.number);
        }
      } else if (curr.isAlternate) {
        ranges.push([curr.number]);
      }

      return ranges;
    }, [])
    .map((range: string[]) => {
      if (range.length > 2 && !wagerDetails) {
        return [range[0], range[range.length - 1]].join("-");
      }
      return range;
    });

export const saddleRowQaLabel = (
  item: string,
  isAlternate: boolean | undefined
) => {
  if (item === "-" || item === "|" || item === "?") {
    return undefined;
  }
  if (item === "all") {
    return "all-saddle-row";
  }
  return isAlternate ? "alternate-saddle-row" : "main-saddle-row";
};

export const getRunnerNameDetails = ({
  item,
  scratches = [],
  showReplacedRunners = false,
  coupledRunners,
  replacedRunners,
  currentSaddleSelection
}: {
  item: string;
  scratches?: string[];
  showReplacedRunners?: boolean;
  coupledRunners: Selection[];
  replacedRunners: Selection[];
  currentSaddleSelection?: Selection;
}) => {
  const isScratched = scratches.includes(item);

  const isCoupledScratched =
    showReplacedRunners &&
    coupledRunners.length > 1 &&
    !!currentSaddleSelection &&
    coupledRunners.some(
      (sel, idx) =>
        idx !== 0 &&
        sel.number === currentSaddleSelection.number &&
        scratches.includes(sel.number)
    );

  const shouldBeSubtleColor =
    currentSaddleSelection?.isSettled ||
    (isScratched &&
      (!showReplacedRunners ||
        currentSaddleSelection?.isAlternate ||
        isCoupledScratched));

  const shouldBeScratched =
    isScratched &&
    (!showReplacedRunners ||
      currentSaddleSelection?.isAlternate ||
      isCoupledScratched);

  const showReplacementName =
    showReplacedRunners &&
    isScratched &&
    (coupledRunners.length === 1 ||
      (coupledRunners.every((sel) => scratches.includes(sel.number)) &&
        /^\d+$/.test(item))) &&
    !currentSaddleSelection?.isAlternate;

  const isWinner = showReplacementName
    ? replacedRunners.some((r) => r.winner)
    : !currentSaddleSelection?.isAlternate && currentSaddleSelection?.winner;

  const replacementNames = replacedRunners
    .filter((r) => !scratches.includes(r.number))
    .map((r) => r.runnerName)
    .join(" / ");

  const currentOdds = showReplacementName
    ? replacedRunners.find((r) => r.winner)?.currentOdds
    : currentSaddleSelection?.currentOdds;

  return {
    showReplacementName,
    currentOdds: isWinner ? currentOdds : undefined,
    fontFamily: (isWinner ? "medium" : "regular") as ParagraphFonts,
    color: shouldBeSubtleColor
      ? "--fd-colors-content-subtle"
      : "--fd-colors-content-default",
    scratched: shouldBeScratched,
    displayName: showReplacementName
      ? replacementNames
      : currentSaddleSelection?.runnerName ?? ""
  };
};
